import { ArrowForwardIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react'
import { getCorrectedValue, getDateString, PopulatedAssessment, StageProgress } from '@hb/shared'
import { useMemo } from 'react'
import { Link } from 'react-router'
import { useNavigate } from '../../hooks/navigation'
import { useMyAssessments } from '../../contexts/user/UserAssessmentsContext'
import { useSignOnComplete } from '../../hooks/backend/assessments/useSignOnComplete'
import { getPregnancyProgress } from '../../utils/assessments/progress'
import { SolidActionButton } from '../Buttons'
import { Loading } from '../Loading'

const ProgressItemView = ({ stage }: { stage: StageProgress }) => {
  const complete = stage.progress === 100
  return (
    <Flex px={3} py={2} w="100%" gap={2}>
      <Text fontFamily="Open Sans" flex={1} fontSize="xl">
        {stage.name}
      </Text>
      <Flex
        fontSize="md"
        alignItems="center"
        lineHeight={1}
        px={3}
        borderRadius="full"
        bg={complete ? 'green.200' : 'gray.200'}>
        <Text color={complete ? 'green.800' : 'gray.800'} fontWeight={500}>
          {complete ? 'Complete' : 'Incomplete'}
        </Text>
      </Flex>
    </Flex>
  )
}

const PregnancyCareProgress = ({ mostRecent }: { mostRecent: PopulatedAssessment }) => {
  const {
    user: { user },
  } = useMyAssessments()
  const signOnData = useSignOnComplete(user, mostRecent)
  const progressBars = useMemo(
    () => getPregnancyProgress(mostRecent, signOnData),
    [mostRecent, signOnData],
  )
  const edd = useMemo(() => {
    const edd = getCorrectedValue(mostRecent, 'delivery.edd')
    return edd ? getDateString(edd) : 'Not set'
  }, [mostRecent])
  return (
    <>
      <Flex bg="gray.50" px={3} py={2} w="100%" gap={2}>
        <Text fontFamily="hero-new" fontWeight={500} color="gray.600" flex={1} fontSize="lg">
          {mostRecent.deliveredOn
            ? `Delivered on ${getDateString(mostRecent.deliveredOn, 'short')}`
            : `EDD: ${edd}`}
        </Text>
      </Flex>
      <Divider />
      <ProgressItemView stage={progressBars.questions} />
      <Divider />
      <ProgressItemView stage={progressBars.view} />
      <Divider />
      <ProgressItemView stage={progressBars.signOn} />
    </>
  )
}

const PregnancyCareContent = () => {
  const { mostRecent, loading } = useMyAssessments()
  const navigate = useNavigate()
  if (!mostRecent && loading) {
    return <Loading text="Loading pregnancy..." />
  }
  return (
    <>
      {mostRecent ? (
        <PregnancyCareProgress mostRecent={mostRecent} />
      ) : (
        <VStack align="flex-start" px={4} py={3}>
          <Text fontFamily="Open Sans" fontSize="lg">
            Click the button below to go to the pregnancy page and complete your pregnancy
            questionnaire.
          </Text>
          <Flex w="100%">
            <SolidActionButton ml="auto" onClick={() => navigate('/pregnancy')}>
              Begin Pregnancy Questionnaire
            </SolidActionButton>
          </Flex>
        </VStack>
      )}
      <Divider />
    </>
  )
}

export const PregnancyCareCard = () => (
  <Card>
    <CardHeader borderBottom="1px solid" borderColor="gray.200" py={2} px={4} fontSize="xl">
      <Flex align="center" w="100%">
        <Link to="/gyn">
          <Button color="gray.600" variant="link" fontSize="2xl">
            Pregnancy Care
          </Button>
        </Link>
        <Box ml="auto">
          <Link to="/pregnancy">
            <IconButton
              bg="green.500"
              color="white"
              borderRadius="full"
              aria-label="Go to Pregnancy Care"
              icon={<ArrowForwardIcon filter="drop-shadow(1px 1px 3px #00000066)" w={6} h={6} />}
            />
          </Link>
        </Box>
      </Flex>
    </CardHeader>
    <CardBody p={0}>
      <PregnancyCareContent />
    </CardBody>
  </Card>
)
